import { Bot, Crown, Loader2, Sparkles } from "lucide-react";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SubagentNode {
  agent_id: string;
  slug: string;
  name: string;
}

/**
 * Slug do subagent default do ``deepagents`` (sempre injetado pelo
 * runtime, alem dos subagents montados a partir de
 * ``team_session_agents``). Quando o supervisor delega para ele, o SSE
 * emite ``delegation { to_slug: "general-purpose" }``.
 */
const GENERAL_PURPOSE_SLUG = "general-purpose";

export type NodeState = "idle" | "active" | "waiting";

/**
 * Estado visual do diagrama, derivado do fluxo de eventos SSE. Vive no
 * COMPONENTE PAI (``TeamChatView``), atualizado por evento via
 * ``teamDiagramReducer`` (functional setState), NUNCA via "ultimo evento"
 * (``lastEvent``) -- ver nota em ``teamDiagramReducer``.
 */
export interface TeamDiagramState {
  supervisorState: NodeState;
  /** Slugs dos subagentes ativos no turno (acumulado entre delegacoes). */
  activeSlugs: Set<string>;
}

export const INITIAL_TEAM_DIAGRAM_STATE: TeamDiagramState = {
  supervisorState: "idle",
  activeSlugs: new Set(),
};

/**
 * Reducer PURO do estado do diagrama a partir de um evento SSE. Aplicar UM
 * evento por chamada, acumulando.
 *
 *  - ``agent_started``: supervisor = active; zera os subagentes.
 *  - ``delegation { to_slug }``: supervisor = waiting; ADICIONA o slug ao Set
 *    (nao sobrescreve!). Delegacoes PARALELAS (2+ tool calls ``task`` no mesmo
 *    turno) emitem 2+ ``delegation`` quase no mesmo tick -> precisam acumular.
 *  - ``delegation_completed { to_slug }``: subagente terminou -> REMOVE o slug
 *    do Set (o no volta a ocioso). Se nao sobrar nenhum subagente ativo, o
 *    supervisor volta a ``active`` (esta consolidando os resultados).
 *  - ``token``/``thinking``/``tool_call``/``tool_result``: nao muda estado.
 *  - ``agent_completed``/``message_completed``/``error``/``cancelled``/``done``:
 *    tudo volta a idle (fim do turno).
 *
 * IMPORTANTE (porque um reducer, e nao um ``useEffect([lastEvent])``): manter
 * "o ultimo evento" num estado e reagir a ele PERDE eventos intermediarios
 * quando o React coalesce renders (as duas ``delegation`` paralelas chegam no
 * mesmo tick -> last-write-wins -> so o 2o subagente ficava ativo). Aplicado
 * como ``setState(prev => teamDiagramReducer(prev, ev))`` por evento, o React
 * enfileira e aplica TODAS as funcoes em ordem -> nenhum evento se perde.
 */
export function teamDiagramReducer(
  prev: TeamDiagramState,
  ev: { event: string; data: unknown },
): TeamDiagramState {
  const data = (ev.data ?? {}) as Record<string, unknown>;
  switch (ev.event) {
    case "agent_started":
      return { supervisorState: "active", activeSlugs: new Set() };
    case "delegation": {
      const to = typeof data.to_slug === "string" ? data.to_slug : null;
      if (!to || prev.activeSlugs.has(to)) {
        return { ...prev, supervisorState: "waiting" };
      }
      const next = new Set(prev.activeSlugs);
      next.add(to);
      return { supervisorState: "waiting", activeSlugs: next };
    }
    case "delegation_completed": {
      const to = typeof data.to_slug === "string" ? data.to_slug : null;
      if (!to || !prev.activeSlugs.has(to)) return prev;
      const next = new Set(prev.activeSlugs);
      next.delete(to);
      // Sem subagentes ativos -> supervisor voltou a trabalhar (consolida).
      return {
        supervisorState: next.size === 0 ? "active" : "waiting",
        activeSlugs: next,
      };
    }
    case "agent_completed":
    case "message_completed":
    case "error":
    case "cancelled":
    case "done":
      return INITIAL_TEAM_DIAGRAM_STATE;
    default:
      return prev;
  }
}

interface Props {
  /** Subagentes listados na team session, na ordem de posicao. */
  subagents: SubagentNode[];
  /** Slug do agente supervisor (quando explicito). Quando vazio, eh "neutro". */
  supervisorSlug?: string | null;
  /** Estado do supervisor (idle/active/waiting), derivado no pai. */
  supervisorState: NodeState;
  /** Slugs dos subagentes ativos agora (delegacoes acumuladas). */
  activeSlugs: Set<string>;
}

/**
 * Diagrama do time supervisor <-> subagentes (PRESENTACIONAL). Renderiza:
 *  - 1 node "Supervisor" no topo
 *  - N nodes de subagentes em grid abaixo, com linha (visual) ligando ambos
 *
 * Estado por node:
 *  - "active":  trabalhando agora (highlight amarelo + spinner)
 *  - "waiting": delegou e esta esperando resposta (highlight azul, apagado)
 *  - "idle":    nao envolvido nessa msg (cinza)
 *
 * O estado (``supervisorState`` + ``activeSlugs``) e calculado no pai via
 * ``teamDiagramReducer`` -- aqui so renderizamos.
 */
export function TeamDiagram({
  subagents,
  supervisorSlug,
  supervisorState,
  activeSlugs,
}: Props) {
  const supervisorLabel = useMemo(() => {
    if (supervisorSlug) return supervisorSlug;
    return "supervisor neutro";
  }, [supervisorSlug]);

  // Sempre injeta o "general-purpose" como ultimo node. Ele eh um
  // subagent default do ``deepagents`` (nao esta em team_session_agents,
  // mas o supervisor pode delegar pra ele para pesquisa amplas /
  // multi-step que nao precisam de um especialista do time).
  const nodes = useMemo<SubagentNode[]>(() => {
    return [
      ...subagents,
      {
        agent_id: "__general_purpose__",
        slug: GENERAL_PURPOSE_SLUG,
        name: "Agente de uso geral (default do runtime)",
      },
    ];
  }, [subagents]);

  return (
    <div className="shadow-card rounded-2xl border border-line bg-surface p-4">
      <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
        Diagrama do time
      </div>
      <div className="flex flex-col items-center gap-3">
        <div className="w-1/3 min-w-0">
          <NodeCard
            icon={<Crown className="h-4 w-4" />}
            label={supervisorLabel}
            role="supervisor"
            state={supervisorState}
          />
        </div>
        <div className="h-3 w-px bg-line" aria-hidden="true" />
        <div className="flex w-full flex-wrap justify-center gap-2">
          {nodes.map((sa) => {
            const isGeneral = sa.slug === GENERAL_PURPOSE_SLUG;
            return (
              <div
                key={sa.agent_id}
                className="flex min-w-0 grow-0 basis-1/3 max-w-[calc(33.333%-0.5rem)]"
              >
                <NodeCard
                  icon={
                    isGeneral ? (
                      <Sparkles className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )
                  }
                  label={sa.slug}
                  title={sa.name}
                  role="subagent"
                  state={activeSlugs.has(sa.slug) ? "active" : "idle"}
                  muted={isGeneral}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface NodeCardProps {
  icon: React.ReactNode;
  label: string;
  title?: string;
  role: "supervisor" | "subagent";
  state: NodeState;
  /** Visual mais discreto (general-purpose default do runtime). */
  muted?: boolean;
}

function NodeCard({ icon, label, title, role, state, muted }: NodeCardProps) {
  return (
    <div
      title={title ?? label}
      className={cn(
        "flex w-full min-w-0 items-center gap-2 rounded-xl border px-3 py-2 text-xs transition-colors",
        state === "active" &&
          "border-warning-500 bg-warning-50 text-warning-700",
        state === "waiting" &&
          "border-brand-300 bg-brand-50 text-brand-700",
        state === "idle" &&
          (muted
            ? "border-dashed border-line bg-surface-muted text-ink-500"
            : "border-line bg-surface-muted text-ink-700"),
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
      {state === "active" ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
      ) : state === "waiting" ? (
        <Badge variant="outline" className="shrink-0 text-[10px]">
          aguardando
        </Badge>
      ) : (
        <span className="text-[10px] uppercase text-ink-400">
          {role === "supervisor" ? "" : muted ? "default" : "ocioso"}
        </span>
      )}
    </div>
  );
}
